import React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import { Text, createMqStyles, tokens } from '@fhs/ui';
import { IconCheckFill } from '@fhs/ui/src/components/icon';
import { formatOrdinal } from '@fhs/ui/src/utils';

export interface Milestone {
  id: string;
  points: number;
  isCompleted: boolean;
}

interface Props {
  milestones: Milestone[];
  style?: StyleProp<ViewStyle>;
}

export function MilestoneTracker({ milestones, style }: Props) {
  const styles = useMqStyles();

  const getMilestoneTitle = (index: number) => {
    const ordinals = [
      'First',
      'Second',
      'Third',
      'Fourth',
      'Fifth',
      'Sixth',
      'Seventh',
      'Eighth',
      'Ninth',
      'Tenth',
    ];

    if (index < ordinals.length) {
      return `${ordinals[index]} Order`;
    }

    return `${formatOrdinal(index + 1)} Order`;
  };

  if (milestones.length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, style]}>
      {milestones.map((milestone, index) => (
        <React.Fragment key={milestone.id}>
          <View
            key={milestone.id}
            style={[styles.milestoneRow, milestone.isCompleted && styles.successMilestoneRow]}
          >
            <View style={styles.leftSection}>
              <View
                style={[
                  styles.checkmarkContainer,
                  milestone.isCompleted && styles.successCheckmarkContainer,
                ]}
              >
                {milestone.isCompleted ? (
                  <IconCheckFill color={tokens.colors.$white} size={22} />
                ) : (
                  <Text.Heading type="four">{index + 1}</Text.Heading>
                )}
              </View>
              <Text.Ui weight="semibold" size="md" style={styles.milestoneTitle}>
                {getMilestoneTitle(index)}
              </Text.Ui>
            </View>
            <View
              style={[
                styles.pointsContainer,
                milestone.isCompleted && styles.successPointsContainer,
              ]}
            >
              <Text.Ui size="sm" style={styles.pointsText}>
                {milestone.isCompleted && (
                  <Text.Ui size="xs" weight="semibold">
                    🎉{' '}
                  </Text.Ui>
                )}
                <Text.Ui size="sm" weight="semibold">
                  {milestone.points.toLocaleString()}
                </Text.Ui>{' '}
                Points
              </Text.Ui>
            </View>
          </View>
          {index < milestones.length - 1 && <View style={styles.separator} />}
        </React.Fragment>
      ))}
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      width: '100%',
      borderColor: tokens.colors.$blackOpacity10,
      borderWidth: 1,
      borderRadius: 8,
    },
  },
  milestoneRow: {
    $base: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
  },
  successMilestoneRow: {
    $base: {
      backgroundColor: tokens.colors.$successOpacity04,
    },
  },
  leftSection: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
  },
  checkmarkContainer: {
    $base: {
      width: 40,
      height: 40,
      borderRadius: 4,
      backgroundColor: tokens.colors.$houseLight,
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  successCheckmarkContainer: {
    $base: {
      backgroundColor: tokens.colors.$success,
    },
  },
  milestoneTitle: {
    $base: {
      color: tokens.colors.$black,
      flex: 1,
      marginLeft: 12,
    },
  },
  pointsContainer: {
    $base: {
      paddingHorizontal: 12,
      paddingVertical: 8,
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 4,
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  successPointsContainer: {
    $base: {
      backgroundColor: tokens.colors.$successOpacity10,
    },
  },
  pointsText: {
    $base: {
      color: tokens.colors.$black,
    },
  },
  separator: {
    $base: {
      height: 1,
      backgroundColor: tokens.colors.$blackOpacity10,
    },
  },
});
